import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Play, ArrowLeft, FileDown, Music } from 'lucide-react';
import { TEACHINGS, HERO_IMAGES } from '../mock';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import VideoModal from '../components/VideoModal';
import api, { fileUrl } from '../lib/api';
import { toSlug } from '../lib/slug';

export default function SermonSeries() {
  const { slug } = useParams();
  const [remote, setRemote] = useState([]);
  const [videoOpen, setVideoOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);

  const load = useCallback(() => {
    api.listTeachings().then(list => {
      const mapped = list.filter(u => u.video_url).map(u => ({
        id: u.id,
        title: u.title,
        speaker: u.speaker || 'Pastor Oladipo Babalola',
        duration: u.duration || '',
        series: u.series || 'Teaching',
        thumbnail: u.thumbnail_url ? fileUrl(u.thumbnail_url) : HERO_IMAGES.crossSunset,
        videoUrl: fileUrl(u.video_url),
        audioUrl: u.audio_url ? fileUrl(u.audio_url) : null,
        pdfUrl: u.pdf_url ? fileUrl(u.pdf_url) : null,
        description: u.description || '',
        isLocal: true,
      }));
      setRemote(mapped);
    }).catch(() => setRemote([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const merged = useMemo(() => [...remote, ...TEACHINGS], [remote]);
  const items = useMemo(() => merged.filter(t => toSlug(t.series) === slug), [merged, slug]);
  const seriesName = items[0]?.series || '';

  if (!seriesName) {
    return (
      <div className="min-h-screen pt-32 pb-20 bg-ivory">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <p className="text-xs tracking-[0.35em] uppercase text-gold-deep mb-4">Sermon Series</p>
          <h1 className="font-serif-display text-4xl md:text-5xl text-ink mb-5">We couldn&rsquo;t find that series</h1>
          <p className="text-ink/60 mb-8">It may still be in preparation. Browse all teachings instead.</p>
          <Link to="/teachings">
            <Button data-testid="series-back-to-teachings" className="bg-teal-brand hover:bg-teal-deep text-white h-11 px-6">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Teachings
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const featured = items[0];
  const rest = items.slice(1);

  return (
    <div>
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="absolute inset-0">
          <img src={featured?.thumbnail || HERO_IMAGES.bibleStudy2} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B27]/95 via-[#0B1B27]/80 to-[#0B1B27]/40" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 text-white">
          <Link to="/teachings" className="inline-flex items-center gap-1.5 text-xs tracking-[0.3em] uppercase text-gold-brand mb-5 hover:text-white transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> All Teachings
          </Link>
          <p className="text-xs tracking-[0.35em] uppercase text-gold-brand mb-3">Sermon Series</p>
          <h1 data-testid="series-title" className="font-serif-display text-5xl md:text-7xl leading-tight">{seriesName}</h1>
          <p className="mt-5 text-lg md:text-xl text-white/80 max-w-2xl">
            A full teaching journey &mdash; {items.length} {items.length === 1 ? 'message' : 'messages'} to walk through together.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {featured && (
            <div className="grid lg:grid-cols-2 gap-10 items-center mb-16">
              <button type="button" onClick={() => { setActiveVideo(featured); setVideoOpen(true); }} className="relative group text-left">
                <div className="absolute -inset-2 bg-gradient-to-br from-gold-brand/30 to-teal-brand/30 rounded-2xl blur-xl opacity-80" />
                <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black">
                  <img src={featured.thumbnail} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-20 w-20 rounded-full bg-white flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                      <Play className="h-8 w-8 text-teal-deep fill-teal-deep ml-1" />
                    </div>
                  </div>
                </div>
              </button>
              <div>
                <Badge className="bg-gold-brand text-ink font-medium mb-3">Start Here</Badge>
                <h2 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight">{featured.title}</h2>
                <p className="mt-3 text-teal-deep font-medium">{featured.speaker}{featured.duration ? ` · ${featured.duration}` : ''}</p>
                {featured.description && <p className="mt-5 text-lg text-ink/70 leading-relaxed">{featured.description}</p>}
                <div className="mt-6 flex flex-wrap gap-2">
                  <Button data-testid="series-watch-now" onClick={() => { setActiveVideo(featured); setVideoOpen(true); }} className="bg-teal-brand hover:bg-teal-deep text-white h-11 px-5">
                    <Play className="mr-2 h-4 w-4" /> Watch Now
                  </Button>
                  {featured.audioUrl && (
                    <a href={featured.audioUrl} target="_blank" rel="noreferrer">
                      <Button variant="outline" className="h-11 px-5 border-teal-brand text-teal-deep hover:bg-teal-brand hover:text-white"><Music className="h-4 w-4 mr-2" /> MP3</Button>
                    </a>
                  )}
                  {featured.pdfUrl && (
                    <a href={featured.pdfUrl} target="_blank" rel="noreferrer">
                      <Button variant="outline" className="h-11 px-5 border-gold-brand text-gold-deep hover:bg-gold-brand hover:text-ink"><FileDown className="h-4 w-4 mr-2" /> Notes</Button>
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {rest.length > 0 && (
            <>
              <h3 className="font-serif-display text-2xl md:text-3xl text-ink mb-6">Continue the series</h3>
              <div data-testid="series-playlist" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((t, idx) => (
                  <Card key={t.id} onClick={() => t.isLocal ? (setActiveVideo(t), setVideoOpen(true)) : null} className={`hover-lift group overflow-hidden bg-white rounded-2xl border border-ink/5 ${t.isLocal ? 'cursor-pointer' : ''}`}>
                    <div className="relative aspect-video overflow-hidden bg-black">
                      <img src={t.thumbnail} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="h-14 w-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                          <Play className="h-6 w-6 text-teal-deep fill-teal-deep ml-0.5" />
                        </div>
                      </div>
                      <Badge className="absolute top-3 left-3 bg-teal-deep text-white font-medium">Part {idx + 2}</Badge>
                      {t.duration && <div className="absolute bottom-3 right-3 px-2 py-1 rounded bg-black/60 text-white text-xs font-medium">{t.duration}</div>}
                    </div>
                    <div className="p-5">
                      <h4 className="font-serif-display text-xl leading-snug text-ink group-hover:text-teal-deep transition-colors">{t.title}</h4>
                      <p className="text-xs tracking-[0.2em] uppercase text-ink/50 mt-2">{t.speaker}</p>
                      {t.description && <p className="text-sm text-ink/60 mt-2 line-clamp-2">{t.description}</p>}
                    </div>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} videoUrl={activeVideo?.videoUrl} title={activeVideo?.title} isLocal={activeVideo?.isLocal} />
    </div>
  );
}
